import type { Transaction } from '../types';
import { RBA_COVERAGE, resolveRatesForDate, isoDateOnly } from './fxRates';
import type { DatedRates } from './fxRates';

/**
 * One-time repair of rates captured by rolling PAST the end of the RBA bundle.
 *
 * Until the lookup was made strict, a transaction dated in the ten days after the
 * bundle's last date (24 Sep to 3 Oct 2026; the bundle has only ever ended on
 * 2026-09-23) had its rates captured from the bundle's LAST row instead of that
 * date's ECB rate. Those rows are recognisable exactly: `fxSource` 'rba',
 * `fxRateDate` equal to the bundle's last date, and a transaction date after it.
 *
 * Each such row is re-captured with the corrected lookup. Only the rate fields
 * change, plus the caches the capture path DERIVES from that rate, because
 * leaving those would keep the stale rate baked into the USD figures:
 *
 *   fxRateUsd, fxRateAud, fxRateDate, fxSource   the rate itself
 *   price      = entryAmount x fxRateUsd           (BUY/SELL, the stored invariant)
 *   networkFee = rawFee x fxRateUsd                (TRANSFER; rawFee recovered as
 *                                                   networkFee / old fxRateUsd)
 *   feeUsd / feeAud = fee x the fee currency's rates (BUY/SELL with a fee)
 *
 * What the user entered (amounts, currencies, the raw fee, dates, wallets,
 * notes) is never touched. A row whose rate cannot be resolved (ECB unreachable)
 * is left exactly as it is and counted as failed, so the repair can try again.
 */

export const FX_REPAIR_FLAG_KEY = 'mycryptoplanner_fx_repair_v1';

/** The resolver the repair uses; injectable so tests need no network. */
export type RateResolver = (currency: string, date: string) => Promise<DatedRates | null>;

/** True for a row whose rate came from rolling past the bundle end. */
export function isStaleCapture(
  tx: Pick<Transaction, 'date' | 'fxSource' | 'fxRateDate'>,
  bundleLastDate: string = RBA_COVERAGE.lastDate
): boolean {
  const day = isoDateOnly(tx.date);
  return tx.fxSource === 'rba' && tx.fxRateDate === bundleLastDate && !!day && day > bundleLastDate;
}

/** The fields the repair changes on one row. */
export type FxRepairPatch = Partial<Pick<
  Transaction,
  'price' | 'networkFee' | 'fxRateUsd' | 'fxRateAud' | 'fxRateDate' | 'fxSource' | 'feeUsd' | 'feeAud'
>>;

export interface FxRepairResult {
  /** id → the fields to change. Only rows that were repaired appear. */
  patches: Record<string, FxRepairPatch>;
  fixed: number;
  /** Stale rows that could not be resolved this time (left untouched). */
  failed: number;
}

function usable(n: number | undefined): n is number {
  return typeof n === 'number' && isFinite(n) && n > 0;
}

/** A resolution that is itself still the stale bundle rate is no repair. */
function isGood(r: DatedRates | null, bundleLastDate: string): r is DatedRates {
  return !!r && usable(r.fxRateUsd) && !(r.fxSource === 'rba' && r.fxRateDate === bundleLastDate);
}

/**
 * Work out the repair for `txs`. Pure apart from the resolver: returns patches,
 * writes nothing. Running it on already-repaired data finds nothing to do.
 */
export async function planFxRepair(
  txs: readonly Transaction[],
  resolve: RateResolver = resolveRatesForDate,
  bundleLastDate: string = RBA_COVERAGE.lastDate
): Promise<FxRepairResult> {
  const patches: Record<string, FxRepairPatch> = {};
  let fixed = 0;
  let failed = 0;

  for (const tx of txs) {
    if (!isStaleCapture(tx, bundleLastDate)) continue;

    const code = (tx.entryCurrency || 'USD').toUpperCase();
    const r = await resolve(code, tx.date);
    if (!isGood(r, bundleLastDate)) { failed++; continue; }

    const patch: FxRepairPatch = {
      fxRateUsd: r.fxRateUsd,
      fxRateDate: r.fxRateDate,
      fxSource: r.fxSource,
      ...(r.fxRateAud != null ? { fxRateAud: r.fxRateAud } : {}),
    };

    if (tx.type === 'TRANSFER') {
      if (usable(tx.networkFee) && usable(tx.fxRateUsd)) {
        const rawFee = tx.networkFee / tx.fxRateUsd;
        patch.networkFee = rawFee * r.fxRateUsd;
      }
    } else if (usable(tx.entryAmount)) {
      patch.price = tx.entryAmount * r.fxRateUsd;
    }

    // The fee was captured by the same stale lookup, in its own currency.
    if (tx.type !== 'TRANSFER' && usable(tx.fee) && tx.feeCurrency) {
      const feeCode = tx.feeCurrency.toUpperCase();
      const rf = feeCode === code ? r : await resolve(feeCode, tx.date);
      if (!isGood(rf, bundleLastDate)) { failed++; continue; } // all or nothing per row
      patch.feeUsd = tx.fee * rf.fxRateUsd;
      if (rf.fxRateAud != null) patch.feeAud = tx.fee * rf.fxRateAud;
    }

    patches[tx.id] = patch;
    fixed++;
  }

  return { patches, fixed, failed };
}

// ── Run-once record ────────────────────────────────────────────────────────────

interface RepairRecord { [userId: string]: { at: string; fixed: number } }

function readRecord(): RepairRecord {
  try {
    const raw = localStorage.getItem(FX_REPAIR_FLAG_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Whether the repair has already completed for `userId` on this device. */
export function fxRepairDone(userId: string): boolean {
  return !!readRecord()[userId];
}

/** Record a completed repair for `userId`. */
export function markFxRepairDone(userId: string, fixed: number): void {
  try {
    const rec = readRecord();
    rec[userId] = { at: new Date().toISOString(), fixed };
    localStorage.setItem(FX_REPAIR_FLAG_KEY, JSON.stringify(rec));
  } catch {
    /* storage unavailable: the repair is idempotent, so running again is harmless */
  }
}
